import { useState, useRef, useEffect } from 'react';

export const useDragDrop = (initialItems = []) => {
  const [items, setItems] = useState(initialItems);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Touch handling
  const touchStartPos = useRef({ x: 0, y: 0 });
  const dragStartTime = useRef(0);
  const isTouchDevice = 'ontouchstart' in window;

  // Update items when initialItems change
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const handleDragStart = (e, item, index) => {
    console.log('useDragDrop: handleDragStart called', { item: item.name || item.id, index });
    setDraggedItem({ item, index });
    setIsDragging(true);
    
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', '');
    }
  };

  const handleTouchStart = (e, item, index) => {
    console.log('Touch start for:', item.name || item.id, 'at index:', index);
    e.preventDefault();
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    dragStartTime.current = Date.now();
    
    // Start drag immediately for better responsiveness
    setDraggedItem({ item, index });
    setIsDragging(true);
    console.log('Touch drag started for:', item.name || item.id);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !isTouchDevice || !draggedItem) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    
    // Find the drop zone
    let dropZone = element;
    while (dropZone && !dropZone.dataset.dropIndex) {
      dropZone = dropZone.parentElement;
    }
    
    if (dropZone && dropZone.dataset.dropIndex) {
      const index = parseInt(dropZone.dataset.dropIndex);
      if (index !== draggedItem.index) {
        setDraggedOverIndex(index);
        console.log('Touch move over index:', index);
      }
    } else {
      setDraggedOverIndex(null);
    }
  };

  const handleTouchEnd = (e) => {
    if (!isDragging || !isTouchDevice || !draggedItem) return;
    
    console.log('Touch end for:', draggedItem.item.name || draggedItem.item.id);
    const touch = e.changedTouches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    
    // Find the drop zone
    let dropZone = element;
    while (dropZone && !dropZone.dataset.dropIndex) {
      dropZone = dropZone.parentElement;
    }
    
    if (dropZone && dropZone.dataset.dropIndex) {
      const dropIndex = parseInt(dropZone.dataset.dropIndex);
      console.log('Touch drop at index:', dropIndex);
      const reorderOccurred = handleDrop(null, dropIndex);
      // Could potentially use reorderOccurred here for feedback
      return reorderOccurred;
    } else {
      console.log('Touch ended but no valid drop zone found');
      cleanup();
      return false;
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
    
    if (draggedItem && index !== draggedItem.index) {
      setDraggedOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear draggedOverIndex if we're actually leaving the drop zone
    // Check if the related target is not a child of the current target
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDraggedOverIndex(null);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('Drop triggered:', { 
      draggedItem, 
      dropIndex, 
      isDragging,
      currentItems: items.length 
    });
    
    let reorderOccurred = false;
    
    if (draggedItem !== null && dropIndex !== undefined && dropIndex !== draggedItem.index) {
      console.log('Performing reorder from', draggedItem.index, 'to', dropIndex);
      const newItems = [...items];
      const [removed] = newItems.splice(draggedItem.index, 1);
      newItems.splice(dropIndex, 0, removed);
      setItems(newItems);
      console.log('Items reordered:', newItems.map(item => item.name || item.id));
      reorderOccurred = true;
    } else {
      console.log('Drop conditions not met:', {
        'draggedItem is null': draggedItem === null,
        'dropIndex undefined': dropIndex === undefined,
        'same index': draggedItem && dropIndex === draggedItem.index
      });
    }
    
    cleanup();
    return reorderOccurred;
  };

  const cleanup = () => {
    setDraggedItem(null);
    setDraggedOverIndex(null);
    setIsDragging(false);
  };

  const moveItem = (fromIndex, toIndex) => {
    const newItems = [...items];
    const [removed] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, removed);
    setItems(newItems);
  };

  const resetItems = (newItems) => {
    setItems(newItems);
    cleanup();
  };

  return {
    items,
    draggedItem,
    draggedOverIndex,
    isDragging,
    isTouchDevice,
    handleDragStart,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleDragOver,
    handleDragLeave,
    handleDragEnter,
    handleDrop,
    moveItem,
    resetItems,
    setItems
  };
};